import { useCallback, useEffect, useMemo, useState } from "react";
import { db } from "~/db/db";
import { useGetAutosservico } from "~hooks/useSincronizacao";
import { autosservicoMock } from "../mocks/new/Autosservico.mock";
import { meses, formatMes, fmtPctVsM1 } from "../utils/formatters";
import { extractUpdatedAt } from "../utils/extractUpdatedAt";
import useVendedor from "~fetchs/useVendedor";
import { usarMock } from "../utils/dataSource";

const barPercentage = 0.5;
const categoryPercentage = 0.7;
const maxGruposGrafico = 5;


const coresGrupo = {
  WEB_CLIENTE:  "#9ACA3C",
  WEB_VENDEDOR: "#FFC300",
  ATIVOS:       "#034EA2",
  IMO:          "#FF5733",
  RECEPTIVOS:   "#1ABC9C",
  CONTRATOS:    "#900C3F",
  SERVIÇOS:     "#8E44AD",
  WHATSAPP:     "#E91E63",
  FIXA:         "#4A4A4A",
  CPP_VE:       "#6D4C41",
  REMOTOS:      "#607D8B",
  OUTROS:       "#8E9CB0",
};
const corPadrao = "#AAAAAA";

const ordemGrupos = [
  "WEB_CLIENTE", "WEB_VENDEDOR", "ATIVOS", "IMO", "RECEPTIVOS",
  "CONTRATOS", "CPP_VE", "SERVIÇOS", "WHATSAPP", "REMOTOS", "OUTROS",
];

const colunasListagem = [
  { key: "cliente",         label: "Cliente"                      },
  { key: "media3m",         label: "Média Pedidos últimos 3M"     },
  { key: "pedidosMesAtual", label: "Pedidos Mês Atual"            },
  { key: "webCliente",      label: "% de Pedidos WEB_CLIENTE"     },
  { key: "webVendedor",     label: "% de Pedidos WEB_VENDEDOR"    },
  { key: "outrosCanais",    label: "% de Pedidos Outros Canais"   },
];

const filtrarValidos = (values) => values.filter((v) => v !== null && v !== undefined);

const avgNonNull = (values) => {
  const valid = filtrarValidos(values);
  return valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
};

const sumNonNull = (values) => {
  const valid = filtrarValidos(values);
  return valid.length ? valid.reduce((a, b) => a + b, 0) : null;
};


const formatarPct = (val) => `${(val * 100).toFixed(1)}%`;

const formatarMedia = (val) =>
  val == null
    ? "0,0"
    : Number(val).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const formatarMix = (val) => {
  if (!val) return null;
  return `Mix: ${Number(val).toFixed(2)}`;
};


const calcularPct = (val, total) => {
  if (val === 0) return null;
  return total === 0 ? null : Math.round((val / total) * 1000) / 10;
};



const ajustarPct100 = (exatos) => {
  const out = exatos.map(() => null);
  const validos = exatos
    .map((v, i) => ({ v, i }))
    .filter((x) => x.v != null);
  if (!validos.length) return out;

  const decimos = validos.map((x) => x.v * 10);
  const piso    = decimos.map((d) => Math.floor(d));
  const restos  = decimos.map((d, k) => d - piso[k]);
  const sobra   = 1000 - piso.reduce((a, b) => a + b, 0);

  const ordem = restos
    .map((r, k) => ({ k, r }))
    .sort((a, b) => b.r - a.r);

  const ajustado = [...piso];
  for (let n = 0; n < sobra && n < ordem.length; n++) {
    ajustado[ordem[n].k] += 1;
  }

  validos.forEach((x, k) => { out[x.i] = ajustado[k] / 10; });
  return out;
};


const construirMapaMeses = (clientes) => {
  const mapaMeses = {};
  const grupos = new Set();
  for (const c of clientes) {
    for (const g of c.grafico ?? []) {
      const mes = g.mes.substring(0, 7);
      const grupo = g.grupoNegocioMapa;
      grupos.add(grupo);
      if (!mapaMeses[mes]) mapaMeses[mes] = { _total: 0 };
      mapaMeses[mes][grupo] = (mapaMeses[mes][grupo] || 0) + g.quantidadeOrdens;
      mapaMeses[mes]._total += g.quantidadeOrdens;
    }
  }
  return { mapaMeses, grupos };
};


const consolidarGruposMinoritarios = (mapaMeses, mesesOrdenados, grupos) => {
  const totaisPorGrupo = {};
  for (const grupo of grupos) {
    totaisPorGrupo[grupo] = mesesOrdenados.reduce((soma, m) => soma + (mapaMeses[m][grupo] || 0), 0);
  }

  const ranking = Object.entries(totaisPorGrupo)
    .filter(([g]) => g !== "OUTROS")
    .sort(([, a], [, b]) => b - a)
    .map(([g]) => g);

  const gruposPrincipais = new Set(ranking.slice(0, maxGruposGrafico));

  for (const grupo of ranking.slice(maxGruposGrafico)) {
    for (const m of mesesOrdenados) {
      if (mapaMeses[m][grupo]) {
        mapaMeses[m]["OUTROS"] = (mapaMeses[m]["OUTROS"] || 0) + mapaMeses[m][grupo];
        delete mapaMeses[m][grupo];
      }
    }
  }

  gruposPrincipais.add("OUTROS");
  return gruposPrincipais;
};


const ordenarGruposGrafico = (gruposPrincipais) =>
  [
    ...ordemGrupos.filter((g) => gruposPrincipais.has(g)),
    ...[...gruposPrincipais].filter((g) => !ordemGrupos.includes(g) && g !== "OUTROS"),
    "OUTROS",
  ].filter((g, i, arr) => arr.indexOf(g) === i);


const definirGrupoTopoMes = (mesesOrdenados, gruposOrdenados, mapaMeses) => {
  const grupoTopoMes = {};
  for (const m of mesesOrdenados) {
    for (let i = gruposOrdenados.length - 1; i >= 0; i--) {
      if (calcularPct(mapaMeses[m][gruposOrdenados[i]] || 0, mapaMeses[m]._total) !== null) {
        grupoTopoMes[m] = gruposOrdenados[i];
        break;
      }
    }
  }
  return grupoTopoMes;
};

export const montarDadosGrafico = (clientes) => {
  const { mapaMeses, grupos } = construirMapaMeses(clientes);
  const mesesOrdenados   = Object.keys(mapaMeses).sort();
  const labels           = mesesOrdenados.map(formatMes);
  const gruposPrincipais = consolidarGruposMinoritarios(mapaMeses, mesesOrdenados, grupos);
  const gruposOrdenados  = ordenarGruposGrafico(gruposPrincipais);
  const grupoTopoMes     = definirGrupoTopoMes(mesesOrdenados, gruposOrdenados, mapaMeses);


  const pctPorMes = Object.fromEntries(
    mesesOrdenados.map((m) => {
      const total = mapaMeses[m]._total;
      const exatos = gruposOrdenados.map((grupo) => {
        const val = mapaMeses[m][grupo] || 0;
        return val === 0 || total === 0 ? null : (val / total) * 100;
      });
      return [m, ajustarPct100(exatos)];
    })
  );

  return {
    labels,
    datasets: gruposOrdenados.map((grupo, gi) => ({
      label:              `% ${grupo}`,
      data:               mesesOrdenados.map((m) => pctPorMes[m][gi]),
      backgroundColor:    coresGrupo[grupo] || corPadrao,
      stack:              "stack",
      barPercentage,
      categoryPercentage,
      borderRadius:       (ctx) =>
        grupoTopoMes[mesesOrdenados[ctx.dataIndex]] === grupo
          ? { topLeft: 3, topRight: 3, bottomLeft: 0, bottomRight: 0 }
          : 0,
    })),
  };
};

const extrairPeriodo = (clientes) => {
  const maxDate = clientes
    .flatMap((c) => c.grafico ?? [])
    .reduce((max, g) => {
      const d = new Date(g.mes);
      return !Number.isNaN(d.getTime()) && (!max || d > max) ? d : max;
    }, null);
  return maxDate ? `${meses[maxDate.getMonth()]}/${maxDate.getFullYear()}` : "";
};

const montarItensCard = (card, titlePedidos) => {
  if (!card) return [];
  const {
    totalPedidos, percentTotalPedidos,
    percentPedidosWebCliente, mixPedidosWebCliente,
    percentPedidosWebVendedor, mixPedidosWebVendedor,
    percentOutros, mixOutros,
  } = card;
  return [
    { title: titlePedidos,                 value: `${totalPedidos} pedidos`,               subtitle: fmtPctVsM1(percentTotalPedidos),     color: "navy"  },
    { title: "% de Pedidos WEB_CLIENTE",   value: formatarPct(percentPedidosWebCliente),   subtitle: formatarMix(mixPedidosWebCliente),   color: "green" },
    { title: "% de Pedidos WEB_VENDEDOR",  value: formatarPct(percentPedidosWebVendedor),  subtitle: formatarMix(mixPedidosWebVendedor),  color: "amber" },
    { title: "% de Pedidos Outros Canais", value: formatarPct(percentOutros),              subtitle: formatarMix(mixOutros),              color: "blue"  },
  ];
};

const montarSecoesStats = (cardRota, cardTodos, period) => {
  if (!cardRota && !cardTodos) return [];

  const cardsRota = montarItensCard(cardRota, "Pedidos da Rota");

  return [
    {
      id: "autosservico",
      title: "Autosserviço",
      period,
      actions: [
        { key: "rota",  label: "Clientes da Rota",  active: true,  cards: cardsRota },
        { key: "todos", label: "Todos os Clientes", active: false, cards: montarItensCard(cardTodos, "Total de Pedidos") },
      ],
      cards: cardsRota,
    },
  ];
};

const montarDadosListagem = (clientes) => ({
  autosservicos: {
    columns: colunasListagem,
    rows: clientes.map((c) => ({
      id: c.id,
      cliente: `${c.codigo} - ${c.razaoSocial}`,
      media3m: formatarMedia(c.media3Meses),
      media3mRaw: c.media3Meses ?? 0,
      pedidosMesAtual: c.pedidosMesAtual,
      webCliente: `${Math.round(c.percentPedidosWebCliente * 100)}%`,
      webVendedor: `${Math.round(c.percentPedidosWebVendedor * 100)}%`,
      outrosCanais: `${Math.round(c.percentOutros * 100)}%`,
    })),
  },
});

const mergeCards = (cards) => {
  if (!cards.length) return null;
  return {
    totalPedidos: sumNonNull(cards.map((c) => c?.totalPedidos)),
    percentTotalPedidos: avgNonNull(cards.map((c) => c?.percentTotalPedidos)),
    percentPedidosWebCliente: avgNonNull(cards.map((c) => c?.percentPedidosWebCliente)),
    mixPedidosWebCliente: avgNonNull(cards.map((c) => c?.mixPedidosWebCliente)),
    percentPedidosWebVendedor: avgNonNull(cards.map((c) => c?.percentPedidosWebVendedor)),
    mixPedidosWebVendedor: avgNonNull(cards.map((c) => c?.mixPedidosWebVendedor)),
    percentOutros: avgNonNull(cards.map((c) => c?.percentOutros)),
    mixOutros: avgNonNull(cards.map((c) => c?.mixOutros)),
  };
};

const mergeClienteItems = (clienteItems, tag) => {
  const byCode = new Map();
  for (const item of clienteItems) {
    const key = item.codigo ?? String(item.id);
    if (!byCode.has(key)) byCode.set(key, []);
    byCode.get(key).push(item);
  }
  return Array.from(byCode.values()).map((group) => {
    const first = group[0];
    const graficoMap = new Map();
    for (const item of group) {
      for (const g of item.grafico ?? []) {
        const k = `${g.grupoNegocioMapa}|${g.mes}`;
        if (!graficoMap.has(k)) graficoMap.set(k, { ...g, quantidadeOrdens: 0 });
        graficoMap.get(k).quantidadeOrdens += g.quantidadeOrdens ?? 0;
      }
    }
    return {
      ...first,
      tag,
      media3Meses: avgNonNull(group.map((g) => g.media3Meses)),
      pedidosMesAtual: sumNonNull(group.map((g) => g.pedidosMesAtual)),
      percentPedidosWebCliente: avgNonNull(group.map((g) => g.percentPedidosWebCliente)),
      percentPedidosWebVendedor: avgNonNull(group.map((g) => g.percentPedidosWebVendedor)),
      percentOutros: avgNonNull(group.map((g) => g.percentOutros)),
      grafico: Array.from(graficoMap.values()),
    };
  });
};

const agregarVendedorItems = (allVendedorItems) => {
  const allBases = allVendedorItems.flatMap((v) =>
    (v?.basesAutosservico || []).filter((item) => Boolean(item?.tag)),
  );

  const porTag = { "Card-Rota": [], "Card-Todos": [], Rota: [], Todos: [] };
  for (const item of allBases) {
    if (porTag[item.tag]) porTag[item.tag].push(item);
  }

  return [
    { id: -1, tag: "Card-Rota",  card: mergeCards(porTag["Card-Rota"].map((i) => i.card))  },
    { id: -2, tag: "Card-Todos", card: mergeCards(porTag["Card-Todos"].map((i) => i.card)) },
    ...mergeClienteItems(porTag.Rota,  "Rota"),
    ...mergeClienteItems(porTag.Todos, "Todos"),
  ];
};

const flattenVendedorItems = (items, vendedorSelecionado = null) => {
  if (!Array.isArray(items) || !items.length) return items ?? [];

  const isVendedorGrouped = items.some((item) => Array.isArray(item?.basesAutosservico));
  if (!isVendedorGrouped) return items;

  if (vendedorSelecionado !== null) {
    return items
      .filter((v) => v?.id === vendedorSelecionado)
      .flatMap((v) => (v?.basesAutosservico || []).filter((item) => Boolean(item?.tag)));
  }

  return agregarVendedorItems(items);
};

const extractItems = (response) => {
  const payload = response?.data?.data?.[0] ?? response?.data?.[0] ?? response?.[0];
  return Array.isArray(payload) ? payload : null;
};

export const useSelfServicesData = () => {
  const [rawItems, setRawItems] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { vendedorSelecionado } = useVendedor();


  const hasMock = useMemo(() => {
    return usarMock(autosservicoMock);
  }, []);

  const { data: queryData, isFetching, isRefetching, isError } =
    useGetAutosservico(!hasMock && navigator.onLine, 0, 0, null);

  const apiItems = useMemo(() => {
    if (hasMock) return autosservicoMock?.[0]?.[0]?.data?.[0] ?? [];
    return extractItems(queryData);
  }, [hasMock, queryData]);

  const loadFromDexie = useCallback(async () => {
    try {
      const rows = await db.autosservico.toArray();
      const stored = rows?.[0]?.payload;
      if (stored) setRawItems(stored);
    } catch (err) {
      console.error("Erro ao carregar autosservico do Dexie:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasMock) {
      setRawItems(autosservicoMock?.[0]?.[0]?.data?.[0] ?? []);
      setIsLoading(false);
      return;
    }
    loadFromDexie();
  }, [hasMock, loadFromDexie]);


  useEffect(() => {
    if (hasMock || !navigator.onLine) return;

    if (isFetching || isRefetching) {
      setIsRefreshing(true);
      return;
    }

    setIsRefreshing(false);

    if (!isError && apiItems !== null && apiItems !== undefined) {
      setRawItems(apiItems);
      setIsLoading(false);
      db.autosservico
        .clear()
        .then(() => db.autosservico.add({ payload: apiItems, syncedAt: new Date().toISOString() }))
        .catch((err) => console.error("Erro ao salvar autosservico no Dexie:", err));
    }
  }, [apiItems, hasMock, isError, isFetching, isRefetching]);


  useEffect(() => {
    if (hasMock) return;
    const handleOffline = () => {
      setIsRefreshing(false);
      loadFromDexie();
    };
    window.addEventListener("offline", handleOffline);
    return () => window.removeEventListener("offline", handleOffline);
  }, [hasMock, loadFromDexie]);

  const items = useMemo(
    () => flattenVendedorItems(rawItems ?? [], vendedorSelecionado),
    [rawItems, vendedorSelecionado],
  );

  const cardRota = useMemo(() => items.find((r) => r.tag === "Card-Rota")?.card ?? null, [items]);
  const cardTodos = useMemo(() => items.find((r) => r.tag === "Card-Todos")?.card ?? null, [items]);

  const clientes = useMemo(
    () => items.filter((r) => r.tag === "Rota"),
    [items],
  );

  const todosClientes = useMemo(() => {
    const byCode = new Map();
    for (const r of items) {
      if (r.tag !== "Rota" && r.tag !== "Todos") continue;
      const key = r.codigo ?? String(r.id);
      if (!byCode.has(key) || r.tag === "Todos") byCode.set(key, r);
    }
    return Array.from(byCode.values());
  }, [items]);

  const period = useMemo(() => extrairPeriodo(clientes), [clientes]);

  const statsSections = useMemo(() => montarSecoesStats(cardRota, cardTodos, period), [cardRota, cardTodos, period]);
  const listingData = useMemo(() => montarDadosListagem(clientes), [clientes]);
  const listingDataTodos = useMemo(() => montarDadosListagem(todosClientes), [todosClientes]);

  const updatedAt = useMemo(() => extractUpdatedAt(rawItems), [rawItems]);

  return {
    cardRota,
    cardTodos,
    clientes,
    todosClientes,
    statsSections,
    listingData,
    listingDataTodos,
    updatedAt,
    isLoading,
    isRefreshing,
  };
};
